import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import SideBar from "../components/SideBar";
import DetailsHeaderBar from "../components/DetailsHeaderBar";
import { QRCodeSVG } from "qrcode.react";
import Select from "react-select";
import Snackbar from "@mui/material/Snackbar";
import MuiAlert from "@mui/material/Alert";
import { useNavigate } from "react-router-dom";
import { API_URL } from "../constant";

function CreatePromotion() {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [errors, setErrors] = useState({});
  const [apiError, setApiError] = useState("");
  const [loading, setLoading] = useState(false);
  const [createdPromo, setCreatedPromo] = useState(null);
  const [showPrintModal, setShowPrintModal] = useState(false);
  const [printCount, setPrintCount] = useState(1);
  const [supplierOptions, setSupplierOptions] = useState([]);
  const [productOptions, setProductOptions] = useState([]);
  const [adAgentOptions, setAdAgentOptions] = useState([]);
  const [retailOptions, setRetailOptions] = useState([]);
  const [optionsLoading, setOptionsLoading] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("success");

  const [form, setForm] = useState({
    Supplier: " ",
    BusinessAdAgentNo: " ",
    RetailBusiness: " ",
    Product: " ",
    TopCoin: 0,
    UniqueID: "EILSA",
    quantity: 0,
    status: "New",
    expiryDate: "",
  });

  const api = useMemo(() => {
    const instance = axios.create({
      baseURL: API_URL ,
    });
    instance.interceptors.request.use((config) => {
      const token = localStorage.getItem("auth_token");
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
        config.headers.Accept = "application/json";
      }
      return config;
    });
    return instance;
  }, []);

  useEffect(() => {
    let active = true;
    const normalizeList = (res) => {
      const raw = res?.data;
      const layered = raw?.data ?? raw;
      if (Array.isArray(layered)) return layered;
      if (Array.isArray(raw)) return raw;
      if (layered && typeof layered === "object") {
        const firstArray = Object.values(layered).find(Array.isArray);
        if (Array.isArray(firstArray)) return firstArray;
      }
      return [];
    };

    const loadOptions = async () => {
      setOptionsLoading(true);
      setApiError("");
      try {
        const [suppliersRes, productsRes, adAgentsRes, retailRes] =
          await Promise.all([
            api.get("/superadmin/suppliers"),
            api.get("/superadmin/products"),
            api.get("/superadmin/agents"),
            api.get("/superadmin/retail-businesses"),
          ]);

        console.debug("Suppliers response", suppliersRes?.data);
        console.debug("Products response", productsRes?.data);
        console.debug("Ad agents response", adAgentsRes?.data);
        console.debug("Retail response", retailRes?.data);

        if (!active) return;

        const suppliers = normalizeList(suppliersRes)
          .map((s) => {
            const value =
              s.id ?? s.supplier_id ?? s.retail_business_id ?? s.value;
            const label =
              s.store_name ||
              s.retail_business?.store_name ||
              s.retailBusiness?.store_name ||
              s.retailer?.store_name ||
              s.retail_name ||
              s.business_name ||
              s.retail_business?.business_name ||
              s.retailBusiness?.business_name ||
              s.retailer?.business_name ||
              s.company_name ||
              s.shop_name ||
              s.supplier_name ||
              s.supplierName ||
              s.name ||
              s.full_name ||
              s.display_name ||
              s.company ||
              s.title ||
              s.retail_business?.name ||
              s.retailBusiness?.name ||
              s.retailer?.name ||
              s.retail_name ||
              s.user?.name ||
              [s.user?.first_name, s.user?.last_name]
                .filter(Boolean)
                .join(" ") ||
              (value ? `Supplier ${value}` : "");
            return { value, label };
          })
          .filter((o) => o.value && o.label);
        if (suppliersRes && suppliers.length === 0) {
          const raw = normalizeList(suppliersRes);
          if (raw.length > 0) {
            console.warn(
              "Suppliers response had items but mapping failed",
              raw[0],
            );
          }
        }

        const products = normalizeList(productsRes)
          .map((p) => {
            const name = p.name || p.title || `Product ${p.id || ""}`;
            const code = p.code || p.sku || p.unique_code;
            return {
              value: p.id ?? p.product_id ?? p.value,
              label: code ? `${name} (${code})` : name,
            };
          })
          .filter((o) => o.value && o.label);

        const rawAdAgents = normalizeList(adAgentsRes);
        const adAgents = rawAdAgents
          .map((a) => {
            const value = a.agent_id;
            const name = [a.first_name, a.last_name].filter(Boolean).join(" ");
            const label = name;
            return { value, label };
          })
          .filter((o) => o.value && o.label);
        if (rawAdAgents.length > 0 && adAgents.length === 0) {
          console.warn(
            "Advertising agents response had items but mapping failed",
            rawAdAgents[0],
          );
        }

        if (!optionsLoading && adAgents.length === 0) {
          console.warn(
            "No advertising agents available or failed to map agents",
          );
        }

        const rawRetailers = normalizeList(retailRes);
        const retailers = rawRetailers
          .map((r) => {
            const value =
              r.id ??
              r.user_id ??
              r.retail_business_id ??
              r.uuid ??
              r.user?.id ??
              r.value;
            const name =
              r.store_name ||
              r.retail_business?.store_name ||
              r.business?.store_name ||
              r.display_name ||
              r.business_name ||
              r.company_name ||
              r.name ||
              r.retail_business?.business_name ||
              r.retail_business?.name ||
              r.business?.business_name ||
              r.business?.name ||
              [r.first_name, r.last_name].filter(Boolean).join(" ") ||
              r.user?.name ||
              [r.user?.first_name, r.user?.last_name]
                .filter(Boolean)
                .join(" ") ||
              [r.firstName, r.lastName].filter(Boolean).join(" ");
            const label =
              name ||
              r.email ||
              r.user?.email ||
              r.phone_number ||
              r.phone ||
              (value ? `Retail ${value}` : "");
            return { value, label };
          })
          .filter((o) => o.value && o.label);
        if (rawRetailers.length > 0 && retailers.length === 0) {
          console.warn(
            "Retail businesses response had items but mapping failed",
            rawRetailers[0],
          );
        }

        setSupplierOptions(suppliers);
        setProductOptions(products);
        setAdAgentOptions(adAgents);
        setRetailOptions(retailers);
      } catch (err) {
        if (!active) return;
        const msg =
          err?.response?.data?.message ||
          err?.message ||
          "Failed to load dropdown options";
        setApiError(msg);
        console.error("Failed to load dropdown options", err);
      } finally {
        if (active) setOptionsLoading(false);
      }
    };

    loadOptions();
    return () => {
      active = false;
    };
  }, [api]);

  const statusOptions = useMemo(
    () => [
      { value: "New", label: "New" },
      { value: "Used", label: "Used" },
      { value: "Cancel", label: "Cancel" },
    ],
    [],
  );

  const validateBasicDetails = () => {
    const nextErrors = {};
    const requiredFields = [
      { key: "Product", label: "Product" },
      { key: "Supplier", label: "Supplier Name" },
      { key: "BusinessAdAgentNo", label: "Business Ad Agent" },
      { key: "RetailBusiness", label: "Retail Business" },
      { key: "UniqueID", label: "UniqueID seed" },
      { key: "expiryDate", label: "Expiry Date" },
    ];

    requiredFields.forEach(({ key, label }) => {
      if (!String(form[key] || "").trim())
        nextErrors[key] = `${label} is required`;
    });

    if (!Number(form.TopCoin) || Number(form.TopCoin) <= 0) {
      nextErrors.TopCoin = "TopCoin must be greater than 0";
    }
    if (!Number(form.quantity) || Number(form.quantity) <= 0) {
      nextErrors.quantity = "Quantity must be greater than 0";
    }

    setErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const validateStep = () => {
    if (step === 1) {
      // Add image validation if needed
    }
    return true;
  };

  const nextStep = () => {
    if (step === 1) {
      const isValid = validateBasicDetails();
      if (!isValid) return;
    }
    if (validateStep()) setStep((prev) => Math.min(prev + 1, 5));
    else alert("Please fill all required fields before proceeding.");
  };

  const prevStep = () => setStep((prev) => Math.max(prev - 1, 1));

  const steps = [
    { label: "Basic details", sub: "Enter promotion basic details" },
    {
      label: "Generate QR code and label",
      sub: "Get the QR code and label",
    },
  ];

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
    setErrors((prev) => ({ ...prev, [name]: "" }));
  };

  const generatedRows = useMemo(() => {
    const pad = (val) => String(val || "").trim();
    const code = `${pad(form.Supplier)}-${pad(form.BusinessAdAgentNo)}-${pad(form.RetailBusiness)}-${pad(form.Product)}-${pad(form.TopCoin)}-${pad(form.UniqueID)}-1`;
    const encrypted = btoa(unescape(encodeURIComponent(code))); // simple demo “encrypt”
    return [
      {
        idx: 1,
        Supplier: form.Supplier,
        BusinessAdAgentNo: form.BusinessAdAgentNo,
        RetailBusiness: form.RetailBusiness,
        Product: form.Product,
        TopCoin: form.TopCoin,
        UniqueID: `${form.UniqueID}-1`,
        Status: form.status,
        label: code,
        encrypted,
      },
    ];
  }, [form]);

  const handlePrint = () => {
    setShowPrintModal(true);
  };

  const confirmPrint = () => {
    const qty = Math.max(1, parseInt(printCount, 10) || 1);
    const availableQty = Number(form.quantity);

    if (qty > availableQty) {
      setSnackbarMessage(
        `Make Sure You have enough product Qty. Available: ${availableQty}`,
      );
      setSnackbarSeverity("warning");
      setOpenSnackbar(true);
      return;
    }

    setPrintCount(qty);
    setShowPrintModal(false);
    setTimeout(() => {
      window.print();
      navigate("/manage-promotion");
    }, 0);
  };

  const cancelPrint = () => {
    setShowPrintModal(false);
  };

  const handleSkipPrint = () => {
    navigate("/manage-promotion");
  };

  const handleSnackbarClose = (event, reason) => {
    if (reason === "clickaway") return;
    setOpenSnackbar(false);
  };

  const printable = useMemo(() => {
    const fallback = generatedRows[0];
    if (createdPromo) {
      const qrValue =
        createdPromo.qr_code_url ||
        createdPromo.qr_code ||
        createdPromo.unique_code ||
        fallback?.label;
      return {
        ...fallback,
        ...createdPromo,
        label: createdPromo.unique_code || fallback?.label,
        qrValue,
      };
    }
    return { ...fallback, qrValue: fallback?.label };
  }, [createdPromo, generatedRows]);

  const printPages = useMemo(() => {
    const total = Math.max(1, parseInt(printCount, 12) || 1);
    const copies = Array.from({ length: total }, (_, idx) => idx);
    const pages = [];
    for (let i = 0; i < copies.length; i += 12) {
      pages.push(copies.slice(i, i + 12));
    }
    return pages;
  }, [printCount]);

  const handleCreatePromotion = async () => {
    if (!validateBasicDetails()) return;
    setApiError("");
    setLoading(true);
    try {
      const payload = {
        supplier_id: form.Supplier,
        agent_id: form.BusinessAdAgentNo,
        retail_business_id: form.RetailBusiness,
        product_id: form.Product,
        topcoin_amount: Number(form.TopCoin),
        quantity: Number(form.quantity),
        expires_at: form.expiryDate,
        unique_code_seed: form.UniqueID,
      };

      const res = await api.post("/superadmin/promos", payload);
      const promoData = res.data?.data || res.data?.promo || res.data;
      setCreatedPromo(promoData || null);
      setOpenSnackbar(true);
      setStep(2);
    } catch (err) {
      const msg =
        err?.response?.data?.message ||
        err?.message ||
        "Failed to create promotion";
      setApiError(msg);
      console.error("Failed to create promotion", err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="manage-member-layout">
      <style>
        {`
          @media print {
            body * { visibility: hidden; }
            #print-area, #print-area * { visibility: visible; }
            #print-area { display: block !important; position: absolute; left: 0; top: 0; width: 100%; }
          }
        `}
      </style>
      <SideBar />
      <div className="product-details-main">
        <Snackbar
          open={openSnackbar}
          autoHideDuration={3000}
          onClose={handleSnackbarClose}
          anchorOrigin={{ vertical: "top", horizontal: "right" }}
        >
          <MuiAlert
            severity={snackbarSeverity}
            icon={
              snackbarSeverity === "success" ? (
                <span
                  style={{
                    fontWeight: "bold",
                    fontSize: "18px",
                    color: "rgba(51, 178, 51, 1)",
                  }}
                >
                  ✔
                </span>
              ) : snackbarSeverity === "warning" ? (
                <span
                  style={{
                    fontWeight: "bold",
                    fontSize: "18px",
                    color: "#ff0000", // Red color for warning icon
                  }}
                >
                  ⚠
                </span>
              ) : (
                <span
                  style={{
                    fontWeight: "bold",
                    fontSize: "18px",
                    color: "#dc3545",
                  }}
                >
                  ✘
                </span>
              )
            }
            sx={{
              backgroundColor: "#ffffff", // White background
              color: "#ff0000", // Red text color
              border:
                snackbarSeverity === "success"
                  ? "2px solid rgba(51, 178, 51, 1)"
                  : snackbarSeverity === "warning"
                    ? "2px solid #ff0000" // Red border for warning
                    : "2px solid #dc3545",
              borderRadius: "10px",
              boxShadow: "0 2px 10px rgba(0,0,0,0.1)",
              fontSize: "15px",
              padding: "8px 12px",
              display: "flex",
              alignItems: "center",
              gap: "8px",
              height: "auto",
              minHeight: "25px",
              width: "100%",
              textAlign: "center",
              justifyContent: "center",
              "& .MuiAlert-message": {
                color: "#ff0000", // Ensure message text is red
                padding: 0,
              },
            }}
          >
            {snackbarMessage}
          </MuiAlert>
        </Snackbar>

        <DetailsHeaderBar
          title="Create Promotion"
          subtitle="After creating a new promotion, you can manage it"
          breadcrumbs={[
            { label: "Home", path: "/manage-member" },
            { label: "Manage Promotion", path: "/manage-promotion" },
            { label: "Create Promotion" },
          ]}
        />

        {apiError && (
          <div
            style={{
              margin: "12px 0",
              padding: "10px 12px",
              background: "#fff0f0",
              border: "1px solid #f3b3b3",
              borderRadius: "8px",
              color: "#a30000",
              fontSize: "14px",
            }}
          >
            {apiError}
          </div>
        )}

        <div className="multi-form-container">
          <div className="progress-bar">
            {steps.map((item, i) => {
              const stepNum = i + 1;
              const isActive = step === stepNum;
              const isCompleted = step > stepNum;
              return (
                <div key={i} className="progress-step">
                  <div
                    className={`circle ${
                      isCompleted ? "completed" : isActive ? "active" : ""
                    }`}
                  >
                    {isCompleted ? "✓" : stepNum}
                  </div>
                  <div className="step-text">
                    <p
                      className={`step-label ${isActive ? "active-label" : ""}`}
                    >
                      {item.label}
                    </p>
                    <p
                      className={`step-subtext ${isActive ? "active-sub" : ""}`}
                    >
                      {item.sub}
                    </p>
                  </div>
                  {i < steps.length - 1 && (
                    <div
                      className={`line ${
                        isCompleted ? "line-filled" : "line-empty"
                      }`}
                    ></div>
                  )}
                </div>
              );
            })}
          </div>

          <div className="step-content">
            {step === 1 && (
              <div>
                <h3>Basic Details</h3>
                <hr />
                <div className="form-grid">
                  <div className="form-group">
                    <label>
                      Product<span className="required">*</span>
                    </label>
                    <Select
                      options={productOptions}
                      value={
                        productOptions.find(
                          (o) => String(o.value) === String(form.Product),
                        ) || null
                      }
                      onChange={(opt) => {
                        setForm((prev) => ({
                          ...prev,
                          Product: opt?.value || "",
                        }));
                        setErrors((prev) => ({ ...prev, Product: "" }));
                      }}
                      placeholder="Enter the product"
                      isSearchable
                      isLoading={optionsLoading}
                      classNamePrefix="custom-select"
                      styles={{
                        control: (base, state) => ({
                          ...base,
                          border: "1.17px solid rgba(209, 209, 209, 1)",
                          borderRadius: "17.6px",
                          background: "rgba(253, 253, 253, 1)",
                          minHeight: "38px",
                          boxShadow: state.isFocused ? "none" : "none",
                          "&:hover": "none",
                          fontSize: "14px",
                          color: "rgba(105, 105, 105, 1)",
                        }),
                        valueContainer: (base) => ({
                          ...base,
                          padding: "0 8px",
                        }),
                        singleValue: (base) => ({
                          ...base,
                          color: "black",
                        }),
                        placeholder: (base) => ({
                          ...base,
                          color: "rgba(105, 105, 105, 1)",
                        }),
                        menu: (base) => ({
                          ...base,
                          backgroundColor: "#fff",
                          borderRadius: "6px",
                          marginTop: "2px",
                          zIndex: 9999,
                        }),
                        option: (base, { isSelected, isFocused }) => ({
                          ...base,
                          backgroundColor: isSelected
                            ? "#e6f0ff"
                            : isFocused
                              ? "#e6f0ff"
                              : "#fff",
                          color: isSelected ? "#000" : "#000",
                          cursor: "pointer",
                        }),
                      }}
                    />
                    <span
                      style={{
                        color: "red",
                        fontSize: "12px",
                        marginTop: "4px",
                        display: "block",
                        minHeight: "16px",
                        visibility: errors.Product ? "visible" : "hidden",
                      }}
                    >
                      {errors.Product || ""}
                    </span>
                  </div>
                  <div className="form-group">
                    <label>
                      Supplier Name<span className="required">*</span>
                    </label>
                    <Select
                      options={supplierOptions}
                      value={
                        supplierOptions.find(
                          (o) => String(o.value) === String(form.Supplier),
                        ) || null
                      }
                      onChange={(opt) => {
                        setForm((prev) => ({
                          ...prev,
                          Supplier: opt?.value || "",
                        }));
                        setErrors((prev) => ({ ...prev, Supplier: "" }));
                      }}
                      placeholder="Enter the supplier name"
                      isSearchable
                      isLoading={optionsLoading}
                      classNamePrefix="custom-select"
                      styles={{
                        control: (base, state) => ({
                          ...base,
                          border: "1.17px solid rgba(209, 209, 209, 1)",
                          borderRadius: "17.6px",
                          background: "rgba(253, 253, 253, 1)",
                          minHeight: "38px",
                          boxShadow: state.isFocused ? "none" : "none",
                          "&:hover": "none",
                          fontSize: "14px",
                          color: "rgba(105, 105, 105, 1)",
                        }),
                        valueContainer: (base) => ({
                          ...base,
                          padding: "0 8px",
                        }),
                        singleValue: (base) => ({
                          ...base,
                          color: "black",
                        }),
                        placeholder: (base) => ({
                          ...base,
                          color: "rgba(105, 105, 105, 1)",
                        }),
                        menu: (base) => ({
                          ...base,
                          backgroundColor: "#fff",
                          borderRadius: "6px",
                          marginTop: "2px",
                          zIndex: 9999,
                        }),
                        option: (base, { isSelected, isFocused }) => ({
                          ...base,
                          backgroundColor: isSelected
                            ? "#e6f0ff"
                            : isFocused
                              ? "#e6f0ff"
                              : "#fff",
                          color: isSelected ? "#000" : "#000",
                          cursor: "pointer",
                        }),
                      }}
                    />
                    <span
                      style={{
                        color: "red",
                        fontSize: "12px",
                        marginTop: "4px",
                        display: "block",
                        minHeight: "16px",
                        visibility: errors.Supplier ? "visible" : "hidden",
                      }}
                    >
                      {errors.Supplier || ""}
                    </span>
                  </div>
                  <div className="form-group">
                    <label>
                      BusinessAdAgent<span className="required">*</span>
                    </label>
                    <Select
                      options={adAgentOptions}
                      value={
                        adAgentOptions.find(
                          (o) =>
                            String(o.value) === String(form.BusinessAdAgentNo),
                        ) || null
                      }
                      onChange={(opt) => {
                        setForm((prev) => ({
                          ...prev,
                          BusinessAdAgentNo: opt?.value || "",
                        }));
                        setErrors((prev) => ({
                          ...prev,
                          BusinessAdAgentNo: "",
                        }));
                      }}
                      placeholder={
                        optionsLoading
                          ? "Loading agents..."
                          : "Select business ad agent"
                      }
                      isSearchable
                      isLoading={optionsLoading}
                      classNamePrefix="custom-select"
                      styles={{
                        control: (base, state) => ({
                          ...base,
                          border: "1.17px solid rgba(209, 209, 209, 1)",
                          borderRadius: "17.6px",
                          background: "rgba(253, 253, 253, 1)",
                          minHeight: "38px",
                          boxShadow: state.isFocused ? "none" : "none",
                          "&:hover": "none",
                          fontSize: "14px",
                          color: "rgba(105, 105, 105, 1)",
                        }),
                        valueContainer: (base) => ({
                          ...base,
                          padding: "0 8px",
                        }),
                        singleValue: (base) => ({
                          ...base,
                          color: "black",
                        }),
                        placeholder: (base) => ({
                          ...base,
                          color: "rgba(105, 105, 105, 1)",
                        }),
                        menu: (base) => ({
                          ...base,
                          backgroundColor: "#fff",
                          borderRadius: "6px",
                          marginTop: "2px",
                          zIndex: 9999,
                        }),
                        option: (base, { isSelected, isFocused }) => ({
                          ...base,
                          backgroundColor: isSelected
                            ? "#e6f0ff"
                            : isFocused
                              ? "#e6f0ff"
                              : "#fff",
                          color: isSelected ? "#000" : "#000",
                          cursor: "pointer",
                        }),
                      }}
                    />
                    <span
                      style={{
                        color: "red",
                        fontSize: "12px",
                        marginTop: "4px",
                        display: "block",
                        minHeight: "16px",
                        visibility: errors.BusinessAdAgentNo
                          ? "visible"
                          : "hidden",
                      }}
                    >
                      {errors.BusinessAdAgentNo || ""}
                    </span>
                  </div>
                  <div className="form-group">
                    <label>
                      RetailBusiness<span className="required">*</span>
                    </label>
                    <Select
                      options={retailOptions}
                      value={
                        retailOptions.find(
                          (o) =>
                            String(o.value) === String(form.RetailBusiness),
                        ) || null
                      }
                      onChange={(opt) => {
                        setForm((prev) => ({
                          ...prev,
                          RetailBusiness: opt?.value || "",
                        }));
                        setErrors((prev) => ({ ...prev, RetailBusiness: "" }));
                      }}
                      placeholder="Enter the retail business"
                      isSearchable
                      isLoading={optionsLoading}
                      classNamePrefix="custom-select"
                      styles={{
                        control: (base, state) => ({
                          ...base,
                          border: "1.17px solid rgba(209, 209, 209, 1)",
                          borderRadius: "17.6px",
                          background: "rgba(253, 253, 253, 1)",
                          minHeight: "38px",
                          boxShadow: state.isFocused ? "none" : "none",
                          "&:hover": "none",
                          fontSize: "14px",
                          color: "rgba(105, 105, 105, 1)",
                        }),
                        valueContainer: (base) => ({
                          ...base,
                          padding: "0 8px",
                        }),
                        singleValue: (base) => ({
                          ...base,
                          color: "black",
                        }),
                        placeholder: (base) => ({
                          ...base,
                          color: "rgba(105, 105, 105, 1)",
                        }),
                        menu: (base) => ({
                          ...base,
                          backgroundColor: "#fff",
                          borderRadius: "6px",
                          marginTop: "2px",
                          zIndex: 9999,
                        }),
                        option: (base, { isSelected, isFocused }) => ({
                          ...base,
                          backgroundColor: isSelected
                            ? "#e6f0ff"
                            : isFocused
                              ? "#e6f0ff"
                              : "#fff",
                          color: isSelected ? "#000" : "#000",
                          cursor: "pointer",
                        }),
                      }}
                    />
                    <span
                      style={{
                        color: "red",
                        fontSize: "12px",
                        marginTop: "4px",
                        display: "block",
                        minHeight: "16px",
                        visibility: errors.RetailBusiness
                          ? "visible"
                          : "hidden",
                      }}
                    >
                      {errors.RetailBusiness || ""}
                    </span>
                  </div>
                  <div className="form-group">
                    <label>
                      TopCoin<span className="required">*</span>
                    </label>
                    <input
                      name="TopCoin"
                      value={form.TopCoin}
                      onChange={handleChange}
                      placeholder="Enter topcoin "
                     maxLength={255} />
                    <span
                      style={{
                        color: "red",
                        fontSize: "12px",
                        marginTop: "4px",
                        display: "block",
                        minHeight: "16px",
                        visibility: errors.TopCoin ? "visible" : "hidden",
                      }}
                    >
                      {errors.TopCoin || ""}
                    </span>
                  </div>
                  {/* <div className="form-group">
                    <label>
                      UniqueID seed<span className="required">*</span>
                    </label>
                    <input
                      name="UniqueID" 
                      value={form.UniqueID} 
                      onChange={handleChange}
                      placeholder="Enter unique ID seed"
                     maxLength={255} />
                    <span
                      style={{
                        color: "red",
                        fontSize: "12px",
                        marginTop: "4px",
                        display: "block",
                        minHeight: "16px",
                        visibility: errors.UniqueID ? "visible" : "hidden",
                      }}
                    >
                      {errors.UniqueID || ""}
                    </span>
                  </div> */}

                  <div className="form-group">
                    <label>
                      Quantity<span className="required">*</span>
                    </label>
                    <input
                      type="number"
                      name="quantity"
                      value={form.quantity}
                      onChange={handleChange}
                      placeholder="Enter quantity"
                    />
                    <span
                      style={{
                        color: "red",
                        fontSize: "12px",
                        marginTop: "4px",
                        display: "block",
                        minHeight: "16px",
                        visibility: errors.quantity ? "visible" : "hidden",
                      }}
                    >
                      {errors.quantity || ""}
                    </span>
                  </div>

                  <div className="form-group">
                    <label>
                      Expiry Date<span className="required">*</span>
                    </label>
                    <input
                      type="date"
                      name="expiryDate"
                      value={form.expiryDate}
                      onChange={handleChange}
                      placeholder="Select expiry date"
                     maxLength={255} />
                    <span
                      style={{
                        color: "red",
                        fontSize: "12px",
                        marginTop: "4px",
                        display: "block",
                        minHeight: "16px",
                        visibility: errors.expiryDate ? "visible" : "hidden",
                      }}
                    >
                      {errors.expiryDate || ""}
                    </span>
                  </div>
                  {/* <div className="form-group">
                    <label>
                      Status
                    </label>
                    <input
                      type="text"
                      name="status"
                      value={form.status || "New"}
                      readOnly
                      disabled
                      style={{
                        border: "1.17px solid rgba(209, 209, 209, 1)",
                        borderRadius: "17.6px",
                        background: "rgba(245, 245, 245, 1)",
                        minHeight: "38px",
                        padding: "0 12px",
                        fontSize: "14px",
                        color: "#000",
                        cursor: "not-allowed",
                        opacity: 0.85,
                      }}
                     maxLength={255} />
                  </div> */}
                </div>
              </div>
            )}
            {step === 2 && (
              <div>
                <h3>QR Code and Label</h3>
                <hr />
                <div className="table-container" style={{ padding: "16px" }}>
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "flex-start",
                      flexWrap: "wrap",
                      gap: "12px",
                    }}
                  >
                    {printable && (
                      <div
                        style={{
                          border: "1px solid #ddd",
                          borderRadius: "8px",
                          padding: "12px",
                          display: "flex",
                          flexDirection: "column",
                          alignItems: "center",
                          gap: "6px",
                          pageBreakInside: "avoid",
                          minWidth: "180px",
                        }}
                      >
                        <QRCodeSVG value={printable.qrValue} size={140} />
                        <div
                          style={{
                            textAlign: "center",
                            fontSize: "13px",
                            lineHeight: 1.4,
                            fontWeight: 600,
                          }}
                        >
                          {printable.label}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Navigation Buttons */}
          <div className="btn-row">
            {/* {step > 1 && (
              <button className="btn prev" onClick={prevStep}>
                Previous
              </button>
            )} */}
            {step === 1 && (
              <button
                className="btn next"
                onClick={handleCreatePromotion}
                disabled={!validateStep() || loading}
                style={{
                  opacity: validateStep() && !loading ? 1 : 0.6,
                  cursor:
                    validateStep() && !loading ? "pointer" : "not-allowed",
                }}
              >
                {loading ? "Creating..." : "Create Promotion"}
              </button>
            )}
            {step === 2 && (
              <button
                className="btn prev"
                onClick={handleSkipPrint}
                style={{ marginRight: "8px" }}
              >
                Skip Print
              </button>
            )}
            {step === 2 && (
              <button
                className="btn next"
                onClick={handlePrint}
                disabled={!validateStep()}
                style={{
                  opacity: validateStep() ? 1 : 0.6,
                  cursor: validateStep() ? "pointer" : "not-allowed",
                }}
              >
                Print
              </button>
            )}
          </div>
        </div>
      </div>

      {showPrintModal && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            backgroundColor: "rgba(0,0,0,0.45)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 9999,
          }}
        >
          <div
            style={{
              background: "#fff",
              borderRadius: "12px",
              padding: "20px",
              width: "320px",
              boxShadow: "0 10px 30px rgba(0,0,0,0.18)",
              display: "flex",
              flexDirection: "column",
              gap: "12px",
            }}
          >
            <div style={{ fontSize: "18px", fontWeight: 700 }}>
              Print quantity
            </div>
            <label
              style={{
                display: "flex",
                flexDirection: "column",
                gap: "6px",
                fontSize: "14px",
              }}
            >
              <span>How many copies do you want to print?</span>
              <input
                type="number"
                min={1}
                value={printCount}
                onChange={(e) => setPrintCount(e.target.value)}
                style={{
                  padding: "10px",
                  borderRadius: "8px",
                  border: "1px solid #ccc",
                  fontSize: "14px",
                }}
              />
            </label>
            <div
              style={{
                display: "flex",
                justifyContent: "flex-end",
                gap: "10px",
              }}
            >
              <button
                onClick={cancelPrint}
                style={{
                  padding: "10px 14px",
                  borderRadius: "8px",
                  border: "1px solid #ccc",
                  background: "#f5f5f5",
                  cursor: "pointer",
                }}
              >
                Cancel
              </button>
              <button
                onClick={confirmPrint}
                style={{
                  padding: "10px 14px",
                  borderRadius: "8px",
                  border: "none",
                  background: "#6d31ed",
                  color: "#fff",
                  cursor: "pointer",
                }}
              >
                Print
              </button>
            </div>
          </div>
        </div>
      )}

      {printable && (
        <div id="print-area" style={{ display: "none" }}>
          {printPages.map((page, pageIdx) => (
            <div
              key={pageIdx}
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(3, 1fr)",
                gap: "14px",
                padding: "18px",
                width: "210mm",
                minHeight: "297mm",
                boxSizing: "border-box",
                justifyItems: "center",
                alignItems: "center",
                pageBreakAfter:
                  pageIdx === printPages.length - 1 ? "auto" : "always",
              }}
            >
              {page.map((itemIdx) => (
                <div
                  key={itemIdx}
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    gap: "10px",
                    padding: "12px",
                    border: "1px solid #ddd",
                    borderRadius: "10px",
                    pageBreakInside: "avoid",
                  }}
                >
                  <QRCodeSVG value={printable.qrValue} size={150} />
                  {/* <div style={{ textAlign: "center", fontSize: "14px", fontWeight: 700 }}>
                    {printable.label}
                  </div> */}
                </div>
              ))}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default CreatePromotion;
